'use client'

import createStore from '../libs/create-store'
import { generateUUID } from '../libs/uuid'

export type TModalProps = {
    key?: string
    title?: string
    titleHTML?: React.ReactNode
    description?: string
    width?: string
    height?: string
    onOpenChange?: (value: boolean) => void
    content?: React.ReactNode
    preventOutsideClose?: boolean
}

const DefaultValues: TModalProps = {
    key: undefined,
    title: undefined,
    titleHTML: undefined,
    description: undefined,
    width: '30rem',
    height: undefined,
    onOpenChange: () => { },
    content: undefined,
    preventOutsideClose: false,
}

const store = createStore<TModalProps[]>([])

// Explicit API (instead of spreading the array store) so every accessor is typed consistently.
const modalStore = {
    /** All open modals, bottom-most first. */
    getAll: store.get,
    /** The modal at `index` (default: the bottom-most one), `undefined` if there is none. */
    get: (index: number = 0): TModalProps | undefined => store.get()[index],
    subscribe: store.subscribe,
    /** Hook returning all open modals, bottom-most first. */
    useStore: store.useStore,
    /** Resizes a modal; by default the top-most one (the one whose content is asking). */
    resizeModal: (newWidth: string, newHeight?: string, index?: number) => {
        const modals = store.get()
        const target = index ?? modals.length - 1
        if (target < 0 || target >= modals.length)
            return

        store.set(
            modals.map((modal, i) =>
                i === target
                    ? { ...modal, width: newWidth, ...(newHeight !== undefined ? { height: newHeight } : {}) }
                    : modal
            )
        )
    },
    pushNewModal: (newData: TModalProps) => {
        store.set([
            ...store.get(),
            {
                ...DefaultValues,
                key: generateUUID(),
                ...newData,
            },
        ])
    },
    updateModal: (newData: TModalProps) => {
        store.set([
            {
                ...DefaultValues,
                key: generateUUID(),
                ...newData,
            },
        ])
    },
    resetModal: (index: number | undefined = undefined) => {
        if (index === undefined)
            store.set([])
        else
            store.set(store.get().filter((_, i) => i != index))
    },
    resetLastModal: () => {
        const modals = [...store.get()]
        modals.pop()
        store.set(modals)
    },
}

export default modalStore
